import type { Editor } from '@tiptap/core'
import { type ReactNode, useCallback, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import Icon from '../../general/Icon'
import { Dropdown } from '../../navigation/Dropdown/Dropdown'
import { BadgeCell, LabelCell, MenuPanel, MenuRow } from './DropdownMenu.styles'

type AaAction =
  | 'strikethrough'
  | 'subscript'
  | 'superscript'
  | 'clearFormatting'

type MenuOption = {
  value: AaAction
  badge: ReactNode
  label: string
}

const SubscriptBadge = styled.span`
  font-size: 16px;
  line-height: 1;
  font-weight: 500;
  color: ${({ theme }) => theme.color.neutral.color5};

  sub {
    font-size: 0.65em;
  }
`

const SuperscriptBadge = styled.span`
  font-size: 16px;
  line-height: 1;
  font-weight: 500;
  color: ${({ theme }) => theme.color.neutral.color5};

  sup {
    font-size: 0.65em;
  }
`

const OPTIONS: MenuOption[] = [
  {
    value: 'strikethrough',
    badge: <Icon name="StrikethroughOutlined" size={14} />,
    label: 'Strikethrough'
  },
  {
    value: 'subscript',
    badge: (
      <SubscriptBadge>
        X<sub>2</sub>
      </SubscriptBadge>
    ),
    label: 'Subscript'
  },
  {
    value: 'superscript',
    badge: (
      <SuperscriptBadge>
        X<sup>2</sup>
      </SuperscriptBadge>
    ),
    label: 'Superscript'
  },
  {
    value: 'clearFormatting',
    badge: <Icon name="DeleteOutlined" size={14} />,
    label: 'Clear Formatting'
  }
]

function rowSelected(editor: Editor, value: AaAction): boolean {
  switch (value) {
    case 'strikethrough': {
      return editor.isActive('strike')
    }
    case 'subscript': {
      return editor.isActive('subscript')
    }
    case 'superscript': {
      return editor.isActive('superscript')
    }
    case 'clearFormatting': {
      return false
    }
    default: {
      return false
    }
  }
}

function runAction(editor: Editor, value: AaAction) {
  switch (value) {
    case 'strikethrough': {
      editor.chain().focus().toggleStrike().run()
      break
    }
    case 'subscript': {
      editor.chain().focus().toggleSubscript().run()
      break
    }
    case 'superscript': {
      editor.chain().focus().toggleSuperscript().run()
      break
    }
    case 'clearFormatting': {
      editor.chain().focus().unsetAllMarks().clearNodes().run()
      break
    }
    default: {
      break
    }
  }
}

type AaFormattingDropdownProps = {
  editor: Editor
}

/**
 * “Aa” typography menu — strikethrough, sub/superscript, clear formatting.
 * Pill trigger; menu rows match block-style dropdown (icon + label).
 * Parent toolbar should re-render on editor transactions so row highlights stay in sync.
 */
export function AaFormattingDropdown({ editor }: AaFormattingDropdownProps) {
  const theme = useTheme()
  const selectedBg = theme.color.status.secondary
  const [open, setOpen] = useState(false)

  const handleSelect = useCallback(
    (value: AaAction) => {
      runAction(editor, value)
      setOpen(false)
    },
    [editor]
  )

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomLeft"
      overlayStyle={{ minWidth: 220 }}
      overlay={
        <MenuPanel role="menu" aria-label="Text formatting">
          {OPTIONS.map((opt) => (
            <MenuRow
              key={opt.value}
              type="button"
              role="menuitem"
              $selected={rowSelected(editor, opt.value)}
              $selectedBg={selectedBg}
              onClick={() => {
                handleSelect(opt.value)
              }}
            >
              <BadgeCell>{opt.badge}</BadgeCell>
              <LabelCell>{opt.label}</LabelCell>
            </MenuRow>
          ))}
        </MenuPanel>
      }
    >
      <AaTrigger type="button" aria-label="More text formatting">
        <AaLabel>Aa</AaLabel>
        <Icon name="DownOutlined" size={10} />
      </AaTrigger>
    </Dropdown>
  )
}

const AaTrigger = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.color.neutral.color8};
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  color: ${({ theme }) => theme.color.neutral.color3};
  cursor: pointer;
  display: inline-flex;
  font-size: 16px;
  font-weight: 500;
  gap: 4px;
  height: 32px;
  padding: 0 12px;

  &:hover {
    background: ${({ theme }) => theme.color.neutral.color9};
    border-color: ${({ theme }) => theme.color.neutral.color6};
  }
`

const AaLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.02em;
`
